/**
 * Normalizes user-picked images before upload.
 *
 * iOS hands back HEIC/HEIF files even when the file input's `accept` lists only
 * jpeg/png/webp, because `accept` is a picker hint rather than a restriction.
 * Browsers other than Safari cannot decode HEIC, so an unconverted upload shows
 * as a broken image for most visitors.
 */

const WEB_SAFE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Rewrites a Supabase Storage public URL to the render endpoint, which decodes
 * the source and re-encodes it to a web-safe format at the requested size.
 *
 * This repairs avatars that were stored before upload conversion existed: the
 * renderer decodes HEIC even though browsers cannot, and it serves a resized
 * image rather than the multi-megabyte original. Non-Supabase URLs (Steam
 * avatars, Dicebear, local assets) are returned unchanged.
 */
export function getDisplayImageUrl(
  imageUrl: string | null | undefined,
  size = 256
): string | null {
  if (!imageUrl) return null;

  const marker = '/storage/v1/object/public/';
  const markerIndex = imageUrl.indexOf(marker);
  if (markerIndex === -1) return imageUrl;

  const base = imageUrl.slice(0, markerIndex);
  const path = imageUrl.slice(markerIndex + marker.length);
  if (!path) return imageUrl;

  const params = new URLSearchParams({
    width: String(size),
    height: String(size),
    resize: 'cover',
    quality: '80',
  });

  return `${base}/storage/v1/render/image/public/${path}?${params}`;
}

/** Longest edge, in px, of a stored profile image. Displayed at <=128px. */
const MAX_DIMENSION = 1024;
const JPEG_QUALITY = 0.85;

export type NormalizedImage = {
  blob: Blob;
  /** Extension matching `blob.type` — never taken from the original filename. */
  ext: string;
  contentType: string;
};

function extensionFor(mimeType: string): string {
  switch (mimeType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/gif':
      return 'gif';
    default:
      return 'jpg';
  }
}

/**
 * Decodes via createImageBitmap, which applies EXIF orientation itself, so the
 * re-encoded JPEG is upright without carrying an orientation tag.
 */
async function reencodeToJpeg(file: File): Promise<NormalizedImage> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new Error('UNSUPPORTED_IMAGE');
  }

  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('UNSUPPORTED_IMAGE');
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );
  if (!blob) {
    throw new Error('UNSUPPORTED_IMAGE');
  }

  return { blob, ext: 'jpg', contentType: 'image/jpeg' };
}

/**
 * Returns a web-displayable image for `file`.
 *
 * Web-safe formats are passed through untouched; anything else (HEIC/HEIF, or
 * an unrecognized type) is decoded and re-encoded as JPEG. Throws
 * `UNSUPPORTED_IMAGE` when the browser cannot decode the file at all — Chrome
 * and Firefox cannot decode HEIC, so an iPhone HEIC uploaded there lands here.
 */
export async function normalizeImageForUpload(file: File): Promise<NormalizedImage> {
  if (WEB_SAFE_MIME_TYPES.includes(file.type)) {
    return {
      blob: file,
      ext: extensionFor(file.type),
      contentType: file.type,
    };
  }

  return reencodeToJpeg(file);
}
